import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, ContextualNavigationLayout, ScrollableView } from '@uplift/ui-v2';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ArrowBack } from '@mui/icons-material';
import { StepFormSwitch } from './StepFormSwitch.component';
import { useProfileContext } from '../profile.context';
import { StepProgressBar } from './StepProgressBar.component';
import { PracticeAddressStep } from './PracticeAddressStep.component';
import { EducationStep } from './EducationStep.component';
import { PreviewDrawer } from './Preview.component';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { ProviderMatchingProfileBookmark, patchProviderDetails } from '@uplift/api';
import { PhotoStep } from './PhotoStep/PhotoStep.component';
import { useHistory } from 'react-router';
import { ROUTING_CONFIG } from '@uplift/constants';
import { PROFILE_FUE_STATUS_QUERY_KEY } from '../profile.utils';
import { useQueryClient } from '@tanstack/react-query';
import { LocalStorageService } from '@uplift/services';

export type ProfileFueStepsProps = {
  status: MatchingProfileFueStep;
  onComplete?: () => void;
};

export type MatchingProfileFueStep = Exclude<ProviderMatchingProfileBookmark,
| ProviderMatchingProfileBookmark.initial
| ProviderMatchingProfileBookmark.complete
>;

export const matchingProfileFueSteps: MatchingProfileFueStep[] = [
  ProviderMatchingProfileBookmark.supportedAgeGroups,
  ProviderMatchingProfileBookmark.modalities,
  ProviderMatchingProfileBookmark.specialties,
  ProviderMatchingProfileBookmark.treatmentApproaches,
  ProviderMatchingProfileBookmark.communityExpertise,
  ProviderMatchingProfileBookmark.bio,
  ProviderMatchingProfileBookmark.languages,
  ProviderMatchingProfileBookmark.gender,
  ProviderMatchingProfileBookmark.faiths,
  ProviderMatchingProfileBookmark.ethnicities,
  ProviderMatchingProfileBookmark.therapeuticStyles,
  ProviderMatchingProfileBookmark.pronouns,
  ProviderMatchingProfileBookmark.qualification,
  ProviderMatchingProfileBookmark.locations, // this step include sessionOption
  ProviderMatchingProfileBookmark.workEmail,
  ProviderMatchingProfileBookmark.photo,
];

const DRAWER_LOCAL_STORAGE_KEY = 'profilePreviewDrawerOpen';

export const ProfileFueSteps: React.VFC<ProfileFueStepsProps> = props => {
  const history = useHistory();
  const { classes, cx } = useProfileFueStepsStyles();
  const { classes: headerClasses } = useHeaderStyles();
  const queryClient = useQueryClient();
  const { profile } = useProfileContext();
  const [ drawerOpen, setDrawerOpen ] = useState(() => {
    return LocalStorageService.getItem<boolean>(DRAWER_LOCAL_STORAGE_KEY) ?? false;
  });

  const initialCurrentStep = props.status;
  // avoiding race conditions by using state to store all current step info
  const [ { currentStep, direction, status }, setCurrentStep ] = useState({ currentStep: initialCurrentStep, direction: 'slide-right', status: props.status });

  const initialStepIndex = matchingProfileFueSteps.findIndex(step => step === initialCurrentStep);
  const [ educationFormIndex, setEducationFormIndex ] = useState(() => {
    const degrees = profile.qualification.degrees;
    const educationStepIndex = matchingProfileFueSteps.findIndex(step => step === ProviderMatchingProfileBookmark.qualification);

    if (initialStepIndex < educationStepIndex) {
      return 0;
    }

    return degrees.length;
  });

  const [ addressFormIndex, setAddressFormIndex ] = useState(() => {
    const addressStepIndex = matchingProfileFueSteps.findIndex(step => step === ProviderMatchingProfileBookmark.locations);
    const locations = profile.locations;

    if (initialStepIndex < addressStepIndex) {
      return 0;
    }

    const index = locations.length * 2;

    return index > 0 ? index : 0;
  });

  const onSetDrawerOpen = useCallback((open: boolean) => {
    setDrawerOpen(open);
    LocalStorageService.setItem(DRAWER_LOCAL_STORAGE_KEY, open);
  }, []);

  const isFirst = currentStep === matchingProfileFueSteps[0] && direction === 'slide-right';

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (isFirst) {
      timeout = setTimeout(() => onSetDrawerOpen(true), 500);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [ isFirst, onSetDrawerOpen ]);


  const currentStepIndex = matchingProfileFueSteps.findIndex(step => step === currentStep);
  const nextStep = matchingProfileFueSteps[currentStepIndex + 1];
  const previousStep = matchingProfileFueSteps[currentStepIndex - 1];
  const onNext = useCallback(async () => {
    if (nextStep) {
      if (currentStep === status) {
        patchProviderDetails(profile.user.id, { matchingProfileBookmark: nextStep });
      }

      setCurrentStep({ currentStep: nextStep, direction: 'slide-right', status: currentStep === status ? nextStep : status });

      return;
    }

    await patchProviderDetails(profile.user.id, { matchingProfileBookmark: ProviderMatchingProfileBookmark.complete });
    queryClient.setQueryData(PROFILE_FUE_STATUS_QUERY_KEY(profile.user.id), ProviderMatchingProfileBookmark.complete);
    props.onComplete?.();
  }, [ currentStep, nextStep, profile.user.id, props, queryClient, status ]);
  const onBack = useCallback(() => {
    if (previousStep) {
      setCurrentStep(p => ({ ...p, currentStep: previousStep, direction: 'slide-left' }));
    }
  }, [ previousStep ]);

  const stepComponent = useMemo(() => {
    if (currentStep === ProviderMatchingProfileBookmark.qualification) {
      return (
        <EducationStep
          onNext={onNext}
          onBack={onBack}
          educationFormIndex={educationFormIndex}
          setEducationFormIndex={setEducationFormIndex}
          key={educationFormIndex}
        />
      );
    }

    if (currentStep === ProviderMatchingProfileBookmark.locations) {
      return (
        <PracticeAddressStep
          onNext={onNext}
          onBack={onBack}
          practiceAddressEditIndex={addressFormIndex}
          setPracticeAddressEditIndex={setAddressFormIndex}
          key={addressFormIndex}
        />
      );
    }

    if (currentStep === ProviderMatchingProfileBookmark.photo) {
      return (
        <PhotoStep
          onBack={onBack}
          onNext={onNext}
        />
      );
    }

    return <StepFormSwitch onNext={onNext} onBack={onBack} step={currentStep} />;
  }, [ addressFormIndex, currentStep, educationFormIndex, onBack, onNext ]);

  const onSaveAndFinishLater = useCallback(() => {
    queryClient.invalidateQueries(PROFILE_FUE_STATUS_QUERY_KEY(profile.user.id));
    history.push(ROUTING_CONFIG.dashboard);
  }, [ history, profile.user.id, queryClient ]);

  const childFactory = (child: React.ReactElement) => React.cloneElement(child, { classNames: direction });

  const enableTransitionDelay = direction === 'slide-right' && currentStep === status && drawerOpen;

  return (
    <ContextualNavigationLayout
      headerProps={{
        leftButton: <Button startIcon={<ArrowBack />} onClick={onSaveAndFinishLater}>Save and Finish Later</Button>,
        rightButton: <Button onClick={() => onSetDrawerOpen(!drawerOpen)}>{drawerOpen ? 'Hide' : 'Show'} Profile Preview</Button>,
        centerBlock: <Typography variant="h6" className={headerClasses.headerText}>Create Your Matching Profile</Typography>
      }}
    >
      <Box flex={1} display="flex" flexDirection="column">
        <StepProgressBar currentStepIndex={currentStepIndex} enableTransitionDelay={enableTransitionDelay} />
        <Box className={cx(classes.slideContainer, enableTransitionDelay && 'delay')}>
          <TransitionGroup childFactory={childFactory}>
            <CSSTransition
              key={currentStep}
              timeout={enableTransitionDelay ? 1400 : 900}
              classNames={direction}
            >
              <Box position="absolute" width="100%" height="100%" display="flex" justifyContent="center">
                <ScrollableView>
                  <Box px={2}>
                    <Box maxWidth="588px" width="100%" mx="auto" pt={5} pb={5}>
                      {stepComponent}
                    </Box>
                  </Box>
                </ScrollableView>
              </Box>
            </CSSTransition>
          </TransitionGroup>
        </Box>
      </Box>
      <PreviewDrawer open={drawerOpen} status={status} currentStep={currentStep} enableTransitionDelay={enableTransitionDelay} />
    </ContextualNavigationLayout>
  );

};

const useProfileFueStepsStyles = makeStyles({ name: 'ProfileFueSteps' })(_ => ({
  slideContainer: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',

    '& .slide-right-enter': {
      transform: 'translateX(100%)',
      opacity: 0,
    },
    '& .slide-right-enter-active': {
      transform: 'translateX(0)',
      transition: 'transform 900ms cubic-bezier(0.215, 0.610, 0.355, 1.1), opacity 200ms linear',
      opacity: 1,

    },
    '& .slide-right-exit': {
      transform: 'translateX(0)',
      opacity: 1,
    },
    '& .slide-right-exit-active': {
      transform: 'translateX(-100%)',
      transition: 'transform 900ms cubic-bezier(0.215, 0.610, 0.355, 1.1), opacity 200ms linear',
      opacity: 0,
    },

    '&.delay .slide-right-enter-active, &.delay .slide-right-exit-active': {
      transitionDelay: '500ms',
    },

    '& .slide-left-enter': {
      transform: 'translateX(-100%)',
      opacity: 0
    },
    '& .slide-left-enter-active': {
      transform: 'translateX(0)',
      transition: 'transform 900ms cubic-bezier(0.215, 0.610, 0.355, 1.1), opacity 200ms linear',
      opacity: 1,
    },
    '& .slide-left-exit': {
      transform: 'translateX(0)',
      opacity: 1,
    },
    '& .slide-left-exit-active': {
      transform: 'translateX(100%)',
      transition: 'transform 900ms cubic-bezier(0.215, 0.610, 0.355, 1.1), opacity 200ms linear',
      opacity: 0,
    },
  },
}));

const useHeaderStyles = makeStyles()((theme) => ({
  headerText: {
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    color: theme.palette.darkSlate.main
  },
}));
